import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Input, Modal, Select, TextArea } from "semantic-ui-react";
import { toast } from "../..";
import { DateInput } from "../../components/DateInput/DateInput";
import { constructDate, dateInit, deconstructDate } from "../../models/common";
import { ISRB, newSrbInit, srbActiveOptions } from "../../models/srb";
import { createNewSRB, editSRB } from "../../api/serviceRecordBooks";
import { ICrewMember } from "../../models/crewMember";
import { useSelector } from "react-redux";
import { AppState } from "../../store/configureStore";
import { TranslateProps } from "../../i18n/TranslateProps";
import { UserRole } from "../../actions/authentificationActions";

type Props = {
    open: boolean;
    close: () => void;
    fetchCrewMember: () => Promise<void>;
    crewMember: ICrewMember;
    srb?: ISRB;
    currentActiveSrb?: ISRB;
};

export const SRBFormModal = ({
    open,
    close,
    fetchCrewMember,
    crewMember,
    srb,
    currentActiveSrb,
}: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const intl = useIntl();
    const [newSrb, setNewSrb] = useState(srb ? srb : newSrbInit);
    const [loading, setLoading] = useState(false);
    const [paperIssueDate, setPaperIssueDate] = useState(
        !srb || srb.paperSrbLastIssueDate.includes("0001-")
            ? dateInit
            : deconstructDate(srb.paperSrbLastIssueDate)
    );

    const handleAddSRB = async () => {
        setLoading(true);
        try {
            newSrb.paperSrbLastIssueDate = constructDate(paperIssueDate);
            newSrb.crewMemberCid = crewMember.cid;
            newSrb.crewMemberGuid = crewMember.guid;
            if (currentActiveSrb)
                newSrb.currentActiveSrbSerialNumber = currentActiveSrb.serialNumber;
            await createNewSRB(newSrb);
            toast(intl.formatMessage({ id: "addedNewSRB" }), true);
            fetchCrewMember();
        } catch {
            toast(intl.formatMessage({ id: "unableToCreateSRB" }), false);
        } finally {
            close();
            setLoading(false);
        }
    };

    const handleEditSRB = async () => {
        setLoading(true);
        try {
            newSrb.paperSrbLastIssueDate = constructDate(paperIssueDate);
            newSrb.crewMemberCid = crewMember.cid;
            await editSRB(newSrb);
            toast(intl.formatMessage({ id: "editedSRB" }), true);
            fetchCrewMember();
        } catch {
            toast(intl.formatMessage({ id: "unableToEditSRB" }), false);
        } finally {
            close();
            setLoading(false);
        }
    };

    useEffect(() => {
        setNewSrb(s => ({ ...s, issuingAuthority: user.authority }));
    }, [user.authority]);

    return (
        <Modal
            open={open}
            onClose={() => close()}
            dimmer="blurring"
            size="small"
            closeOnDimmerClick={!loading}
        >
            <Modal.Header>
                {srb ? intl.formatMessage({ id: "editSrb" }) : intl.formatMessage({ id: "addSrb" })}
            </Modal.Header>
            <Modal.Content>
                {srb && (
                    <section id="topAddCertificate" className="input-container">
                        <label>{intl.formatMessage({ id: "status" })}</label>
                        <Select
                            selectOnBlur={false}
                            options={TranslateProps(srbActiveOptions)}
                            value={newSrb.active}
                            onChange={(e, data: any) => {
                                setNewSrb({ ...newSrb, active: data.value });
                            }}
                            disabled={user.role === UserRole.EnforcementAuthorityUser}
                        />
                    </section>
                )}
                <form className="add-certificate-form">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "paperSrbLastIssueAuthority" })}</label>
                        <Input
                            value={newSrb.paperSrbLastIssueAuthority ?? ""}
                            onChange={(e: any) =>
                                setNewSrb({ ...newSrb, paperSrbLastIssueAuthority: e.target.value })
                            }
                            disabled={user.role === UserRole.EnforcementAuthorityUser}
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "paperSrbLastIssueDate" })}</label>
                        <DateInput
                            date={paperIssueDate}
                            setDate={setPaperIssueDate}
                            extendedYears
                            disabled={user.role === UserRole.EnforcementAuthorityUser}
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "paperSrbLastNB" })}</label>
                        <Input
                            value={newSrb.paperSrbLastNB ?? ""}
                            onChange={(e: any) =>
                                setNewSrb({ ...newSrb, paperSrbLastNB: e.target.value })
                            }
                            disabled={user.role === UserRole.EnforcementAuthorityUser}
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "paperSrbNavigationDays" })}</label>
                        <Input
                            value={newSrb.paperSrbNavigationDays ?? ""}
                            onChange={(e: any) =>
                                setNewSrb({ ...newSrb, paperSrbNavigationDays: e.target.value })
                            }
                            disabled={user.role === UserRole.EnforcementAuthorityUser}
                        />
                    </section>
                </form>
                <section id="bottomAddCertificate" className="input-container">
                    <label>{intl.formatMessage({ id: "comments" })}</label>
                    <TextArea
                        value={newSrb.comments ?? ""}
                        onChange={(e: any) => setNewSrb({ ...newSrb, comments: e.target.value })}
                        rows={3}
                        disabled={user.role === UserRole.EnforcementAuthorityUser}
                    />
                </section>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    fluid
                    primary
                    onClick={() => (srb ? handleEditSRB() : handleAddSRB())}
                    content={
                        srb
                            ? intl.formatMessage({ id: "saveChanges" })
                            : intl.formatMessage({ id: "addSrb" })
                    }
                    loading={loading}
                    disabled={loading || user.role === UserRole.EnforcementAuthorityUser}
                />
            </Modal.Actions>
        </Modal>
    );
};
